import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useGeo, formatPrice } from "../lib/locale";
import { CATEGORIES, CONDITIONS, PART_NAMES, YEARS, TID } from "../constants/catalog";
import ImageUploader from "../components/ImageUploader";
import { getRecaptchaToken, recaptchaEnabled } from "../lib/recaptcha";
import { Sparkles, Loader2, ShieldAlert, MapPin, ShieldCheck, TrendingUp } from "lucide-react";

const COUNTRIES_CURR = {
  India: "INR", "United States": "USD", "United Kingdom": "GBP", Germany: "EUR",
  France: "EUR", Italy: "EUR", Spain: "EUR", Netherlands: "EUR",
  Australia: "AUD", Canada: "CAD", Japan: "JPY", Singapore: "SGD",
  "United Arab Emirates": "AED", "Hong Kong": "HKD", Switzerland: "CHF",
  China: "CNY", "South Korea": "KRW", "New Zealand": "NZD", Brazil: "BRL",
  Mexico: "MXN", "South Africa": "ZAR",
};

const SELLER_TYPES = ["End User", "Repair Shop", "Just Liquidator"];

// Read file as base64 (without data: prefix) for AI vision input
const fileFromUrlToBase64 = async (url) => {
  const r = await fetch(url, { credentials: "include" });
  const blob = await r.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = reader.result;
      resolve(s.slice(s.indexOf(",") + 1));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

export default function CreateListing() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const [search] = useSearchParams();
  const isEdit = Boolean(editId) || search.get("edit") === "1";
  const { user, loading } = useAuth();
  const geo = useGeo();
  const [images, setImages] = useState([]);
  const [originalListing, setOriginalListing] = useState(null);
  const [form, setForm] = useState({
    seller_type: "",
    category: "",
    model_number: "",
    model_name: "",
    year: "",
    part_name: "",
    part_custom_text: "",
    condition: "",
    description: "",
    price: "",
    currency: "",
  });
  const [aiBusy, setAiBusy] = useState(false);
  const [priceBusy, setPriceBusy] = useState(false);
  const [priceSuggest, setPriceSuggest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const descRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) nav("/login", { state: { redirect: "/sell" } });
  }, [loading, user, nav]);

  useEffect(() => {
    if (geo && !form.currency) {
      setForm((f) => ({ ...f, currency: geo.currency || "INR" }));
    }
    // eslint-disable-next-line
  }, [geo]);

  // Load existing listing when editing
  useEffect(() => {
    if (!editId || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/listings/${editId}`);
        const l = r.data;
        if (l.user_id !== user.user_id) {
          toast.error("You can only edit your own listings");
          nav(`/listing/${editId}`);
          return;
        }
        if (cancelled) return;
        setOriginalListing(l);
        setForm({
          seller_type: l.seller_type || "",
          category: l.category || "",
          model_number: l.model_number || "",
          model_name: l.model_name || "",
          year: l.year ? String(l.year) : "",
          part_name: l.part_name || "",
          part_custom_text: l.part_custom_text || "",
          condition: l.condition || "",
          description: l.description || "",
          price: l.price ? String(l.price) : "",
          currency: l.currency || "INR",
        });
        setImages(l.images || []);
      } catch {
        toast.error("Couldn't load listing");
      }
    })();
    return () => { cancelled = true; };
  }, [editId, user, nav]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const applyAi = (data) => {
    if (!data) return;
    setForm((f) => ({
      ...f,
      category: CATEGORIES.includes(data.category) ? data.category : f.category,
      model_number: data.model_number || f.model_number,
      year: data.year && Number(data.year) >= 2007 ? String(data.year) : f.year,
      part_name: PART_NAMES.includes(data.part_name) ? data.part_name : f.part_name,
      condition: data.condition?.startsWith("Working")
        ? CONDITIONS[0]
        : data.condition?.startsWith("Partially")
        ? CONDITIONS[1]
        : data.condition?.startsWith("Dead")
        ? CONDITIONS[2]
        : f.condition,
    }));
  };

  const generateDescription = async () => {
    if (!images.length) return toast.error("Add at least one photo first");
    if (!form.category || !form.model_number || !form.part_name) {
      return toast.error("Fill category, model number, and part name first");
    }
    setAiBusy(true);
    try {
      const b64 = await fileFromUrlToBase64(images[0].url);
      const r = await api.post("/ai/describe-listing", {
        image_base64: b64,
        category: form.category,
        model_number: form.model_number,
        year: form.year,
        part_name: form.part_name,
        condition: form.condition,
      });
      if (!r.data?.description) return toast.error("AI couldn't draft. Try again.");
      setForm((f) => ({ ...f, description: r.data.description }));
      if (r.data.matches_model === false) {
        toast("Heads-up: AI thinks the photo may not match your model number. Please re-check.", { icon: "⚠️" });
      } else {
        toast.success("Draft inserted — edit before publishing");
      }
      setTimeout(() => descRef.current?.focus(), 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI description failed");
    } finally {
      setAiBusy(false);
    }
  };

  const suggestPrice = async () => {
    if (!form.category || !form.model_number || !form.part_name || !form.condition) {
      return toast.error("Fill category, model number, part name, and condition first");
    }
    setPriceBusy(true);
    try {
      const r = await api.post("/ai/suggest-price", {
        category: form.category,
        model_number: form.model_number,
        year: form.year,
        part_name: form.part_name,
        condition: form.condition,
        currency: form.currency || geo?.currency || "INR",
        country: geo?.country || "India",
      });
      setPriceSuggest(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI pricing failed");
    } finally {
      setPriceBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (images.length < 2) return toast.error("Please upload at least 2 photos");
    if (!form.seller_type) return toast.error("Please select who you are (seller type)");
    if (form.description.length < 50) return toast.error("Description must be at least 50 characters");
    setSubmitting(true);
    try {
      const mod = await api.post("/ai/moderate-text", { text: form.description }).catch(() => null);
      if (mod?.data?.is_illegal || mod?.data?.mentions_stolen) {
        toast.error("Listing flagged: " + (mod.data.reason || "policy violation"));
        setSubmitting(false);
        return;
      }
      const country = geo?.country || "India";
      const city = geo?.city || "Mumbai";
      const basePayload = {
        category: form.category,
        seller_type: form.seller_type,
        model_number: form.model_number.trim(),
        model_name: form.model_name.trim() || null,
        year: Number(form.year),
        part_name: form.part_name,
        part_custom_text: form.part_name === "Other" ? form.part_custom_text : null,
        condition: form.condition,
        description: form.description.trim(),
        price: form.price ? Number(form.price) : null,
        currency: form.currency || geo?.currency || "INR",
        images,
      };

      if (isEdit && editId) {
        const r = await api.patch(`/listings/${editId}`, basePayload);
        const review = r.data?.review;
        if (review && review.approved === false) {
          toast("Edit submitted — held for review by our AI. Admin will approve shortly.", { icon: "🛡️" });
        } else {
          toast.success("Listing updated");
        }
        nav(`/listing/${editId}`);
        return;
      }

      const payload = {
        ...basePayload,
        city,
        country,
        country_code: geo?.country_code || null,
        location_lat: geo?.lat || null,
        location_lng: geo?.lng || null,
      };
      const token = await getRecaptchaToken("create_listing");
      const r = await api.post("/listings", payload, {
        headers: token ? { "X-Recaptcha-Token": token } : {},
      });
      toast.success("Listing published");
      nav(`/listing/${r.data.listing_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't publish listing");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="pbp-section py-10 md:py-16 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">{isEdit ? "Edit listing" : "Create listing"}</div>
      <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">{isEdit ? "Update your listing" : "List an Apple part"}</h1>
      <p className="mt-3 text-[#1d1d1f]/75 max-w-xl">
        {isEdit
          ? "Material edits (model, category, price changes > 3×, image swap) are AI-reviewed and held for moderator approval before going live again."
          : "Upload your photos first — our AI suggests the model, part and condition to remove guesswork. Review before publishing."}
      </p>

      {isEdit && originalListing?.status === "pending_review" && (
        <div className="mt-5 rounded-2xl border border-[#ff9500]/40 bg-[#ff9500]/10 px-5 py-3 text-sm text-[#7a3f00] flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5" strokeWidth={1.8} /> This listing is currently hidden — awaiting moderator approval after a recent edit.
        </div>
      )}

      <form onSubmit={submit} className="mt-10 space-y-8">
        <Section title="1. Photos" subtitle="Min 2, max 10 &middot; AI auto-suggest from your first photo">
          <ImageUploader
            testId={TID.create.images}
            images={images}
            setImages={setImages}
            onAiSuggest={applyAi}
          />
        </Section>

        <Section title="2. Who are you?" subtitle="Helps buyers find the right kind of seller in search and filters.">
          <div className="grid sm:grid-cols-3 gap-3">
            {SELLER_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`create-seller-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setForm((f) => ({ ...f, seller_type: t }))}
                className={`rounded-2xl border p-4 text-left transition-all ${
                  form.seller_type === t
                    ? "border-[#0071e3] bg-[#0071e3]/8 ring-2 ring-[#0071e3]/30"
                    : "border-[#d2d2d7] hover:border-[#0071e3]/60"
                }`}
              >
                <div className="font-semibold">{t}</div>
                <div className="text-xs text-[#86868b] mt-1">
                  {t === "End User" && "Personal device, one-off sale"}
                  {t === "Repair Shop" && "Repair business with regular stock"}
                  {t === "Just Liquidator" && "Bulk seller / refurb dealer"}
                </div>
              </button>
            ))}
          </div>
        </Section>

        <Section title="3. Part details">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Brand">
              <input value="Apple" disabled className="pbp-input bg-[#f5f5f7] text-[#86868b]" />
            </Field>
            <Field label="Category *">
              <select data-testid={TID.create.category} required value={form.category} onChange={set("category")} className="pbp-input">
                <option value="">Select category</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Model number *" hint="e.g. A2338, A2141">
              <input data-testid={TID.create.modelNumber} required value={form.model_number} onChange={set("model_number")} className="pbp-input" placeholder="A2338" />
            </Field>
            <Field label="Model name" hint="Marketing name buyers search for">
              <input data-testid="create-model-name" value={form.model_name} onChange={set("model_name")} className="pbp-input" placeholder='e.g. MacBook Air M4 13", iPhone 17 Pro' />
            </Field>
            <Field label="Year of manufacturing *">
              <select data-testid={TID.create.year} required value={form.year} onChange={set("year")} className="pbp-input">
                <option value="">Select year</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </Field>
            <Field label="Part name *">
              <select data-testid={TID.create.part} required value={form.part_name} onChange={set("part_name")} className="pbp-input">
                <option value="">Select part</option>
                {PART_NAMES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Condition *">
              <select data-testid={TID.create.condition} required value={form.condition} onChange={set("condition")} className="pbp-input">
                <option value="">Select condition</option>
                {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            {form.part_name === "Other" && (
              <Field label="Custom part name *" className="md:col-span-2">
                <input data-testid={TID.create.partCustom} required value={form.part_custom_text} onChange={set("part_custom_text")} className="pbp-input" />
              </Field>
            )}
          </div>

          {/* Smart warranty notice — centred under conditions */}
          <div className="mt-7 mx-auto max-w-xl rounded-2xl border border-[#ff9500]/30 bg-gradient-to-br from-[#ff9500]/10 to-[#ff9500]/5 px-5 py-4">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-[#a05c00] mt-0.5" strokeWidth={1.8} />
              <div className="text-[13.5px] leading-relaxed text-[#7a3f00]">
                <b className="text-[#5a2e00]">Pre-loved &amp; out-of-warranty.</b> Used parts and
                second-life units typically don&rsquo;t carry an active Apple warranty. We recommend
                you and the buyer agree on a mutual return/replacement window if any
                <span className="font-medium"> in writing inside chat / WhatsApp</span> before
                exchanging money or entering into any kind of transaction.
              </div>
            </div>
          </div>
        </Section>

        <Section title="4. Description" subtitle="50&ndash;2000 characters. Generate with AI, then edit.">
          <div className="flex flex-wrap gap-2 mb-3">
            <button
              type="button"
              data-testid="create-ai-describe"
              onClick={generateDescription}
              disabled={aiBusy || !images.length}
              className="pbp-btn-secondary text-[13px]"
            >
              {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[#0071e3]" strokeWidth={1.8} />}
              {aiBusy ? "Drafting…" : "Generate description with AI"}
            </button>
            {form.description && (
              <button type="button" onClick={() => setForm((f) => ({ ...f, description: "" }))} className="pbp-btn-ghost text-[13px]">
                Clear
              </button>
            )}
          </div>
          <textarea
            ref={descRef}
            data-testid={TID.create.description}
            required minLength={50} maxLength={2000}
            value={form.description} onChange={set("description")}
            rows={7} className="pbp-input resize-y"
            placeholder="Tell buyers about the part — visible scratches, tested state, why you're selling…"
          />
          <div className="text-xs text-[#86868b] mt-1">{form.description.length}/2000</div>
        </Section>

        <Section title="5. Price" subtitle="Use AI Suggest to see typical second-hand pricing for similar parts.">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <button
              type="button"
              data-testid="create-ai-price"
              onClick={suggestPrice}
              disabled={priceBusy}
              className="pbp-btn-secondary text-[13px]"
            >
              {priceBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4 text-[#0071e3]" strokeWidth={1.8} />}
              {priceBusy ? "Estimating…" : "Suggest price with AI"}
            </button>
            {priceSuggest && (
              <div className="flex flex-wrap gap-2 items-center" data-testid="create-price-chips">
                {["low", "mid", "high"].map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, price: String(priceSuggest[k]) }))}
                    className="pbp-chip hover:bg-[#0071e3]/10 hover:text-[#0071e3] cursor-pointer text-[12px] font-medium"
                  >
                    {k.toUpperCase()}: {formatPrice(priceSuggest[k], priceSuggest.currency || form.currency)}
                  </button>
                ))}
                <span className="text-[11px] text-[#86868b] max-w-xs">{priceSuggest.rationale}</span>
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <Field label="Currency">
              <input data-testid={TID.create.currency} disabled value={form.currency} className="pbp-input bg-[#f5f5f7] text-[#1d1d1f]" />
            </Field>
            <Field label="Price" hint="Leave blank for ‘Best Offer’" className="md:col-span-2">
              <input data-testid={TID.create.price} type="number" min="0" step="1" value={form.price} onChange={set("price")} className="pbp-input" placeholder="e.g. 4500" />
            </Field>
          </div>
        </Section>

        <Section title="6. Location" subtitle={isEdit ? "Location is set at the time of publishing and cannot be changed." : "Auto-detected from your IP and locked to prevent fraud."}>
          <div className="rounded-2xl border border-[#d2d2d7] bg-[#f5f5f7] p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-white grid place-items-center text-[#0071e3]">
              <MapPin className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold" data-testid="create-city-display">
                {(isEdit ? originalListing?.city : geo?.city) || "Detecting…"}
                {((isEdit ? originalListing?.city : geo?.city) && (isEdit ? originalListing?.country : geo?.country)) ? ", " : ""}
                <span data-testid="create-country-display">{(isEdit ? originalListing?.country : geo?.country) || ""}</span>
              </div>
              <div className="text-xs text-[#86868b]">
                Listing {isEdit ? "is" : "will publish"} from this location in {form.currency || geo?.currency || "INR"}.
              </div>
            </div>
          </div>
        </Section>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
          <p className="text-xs text-[#86868b] max-w-md">
            By publishing, you agree that the part is genuine Apple hardware and not stolen, that you
            accept our <a className="pbp-link" href="/terms">Terms</a>, and that PartByPart may remove
            listings that violate policy. {recaptchaEnabled() && <span className="inline-flex items-center gap-1 ml-1"><ShieldCheck className="w-3 h-3" />Protected by reCAPTCHA</span>}
          </p>
          <button data-testid={TID.create.submit} disabled={submitting} className="pbp-btn-primary px-7 py-3">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save changes" : "Publish listing"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, subtitle, children }) => (
  <section className="pbp-card p-6 md:p-8">
    <div className="mb-5">
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      {subtitle && <p className="text-sm text-[#86868b] mt-1" dangerouslySetInnerHTML={{ __html: subtitle }} />}
    </div>
    {children}
  </section>
);

const Field = ({ label, hint, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="pbp-label">{label}</span>
    {children}
    {hint && <span className="text-xs text-[#86868b] mt-1 block">{hint}</span>}
  </label>
);
